import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, MapPin, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, priceLabel } from "@/lib/api";
import { beep } from "@/lib/media";
import { useServiceMap } from "@/lib/data";
import { ServiceIcon } from "@/lib/icons";
import { useAuth } from "@/context/AuthContext";

function useCountdown(expiresAt) {
  const calc = () => Math.max(0, Math.round((new Date(expiresAt) - Date.now()) / 1000));
  const [left, setLeft] = useState(calc);
  useEffect(() => {
    const tick = () => setLeft(calc());
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [expiresAt]);
  return left;
}

function OfferModal({ offer, onDone }) {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const svc = useServiceMap()[offer.service_slug];
  const left = useCountdown(offer.expires_at);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    beep();
    const t = setInterval(beep, 3000);
    const orig = document.title;
    const f = setInterval(() => (document.title = document.title === orig ? "NOVO PEDIDO!" : orig), 1000);
    return () => { clearInterval(t); clearInterval(f); document.title = orig; };
  }, []);

  useEffect(() => { if (left === 0) onDone(); }, [left, onDone]);

  const act = async (kind) => {
    setBusy(true);
    try {
      await api.post(`/requests/${offer.id}/${kind}`);
      if (kind === "accept") {
        toast.success("Pedido aceito! Fale com o cliente pelo WhatsApp.");
        refresh();
        navigate(`/pedido/${offer.id}`);
      }
    } catch (e) {
      toast.error(errMsg(e));
      if (e.response?.status === 402) navigate("/plano-pro");
    }
    onDone();
  };

  const mm = String(Math.floor(left / 60)).padStart(1, "0");
  const ss = String(left % 60).padStart(2, "0");
  return (
    <div className="fixed inset-0 z-[1000] flex items-end justify-center bg-slate-900/70 p-3 sm:items-center" data-testid="offer-modal">
      <div className="po-flash w-full max-w-lg rounded-3xl border-4 bg-white p-6 shadow-2xl">
        <div className="flex items-center gap-3 text-orange-600">
          <BellRing className="h-8 w-8 animate-bounce" />
          <p className="font-display text-2xl font-extrabold">Novo pedido!</p>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-orange-600">
            <ServiceIcon icon={svc?.icon} className="h-8 w-8" />
          </span>
          <div>
            <p className="text-xl font-bold text-slate-900" data-testid="offer-service">{offer.service_name}</p>
            <p className="flex items-center gap-1 text-lg text-slate-700" data-testid="offer-location">
              <MapPin className="h-5 w-5" /> {offer.neighborhood}, {offer.city}
              {offer.distance_km != null && ` · ${String(offer.distance_km).replace(".", ",")} km`}
            </p>
          </div>
        </div>
        <p className="mt-4 rounded-2xl bg-orange-500 px-4 py-3 text-center font-display text-2xl font-extrabold text-white" data-testid="offer-price">{priceLabel(offer)}</p>
        {offer.description && <p className="mt-3 rounded-2xl bg-slate-50 p-4 text-lg text-slate-800" data-testid="offer-description">{offer.description}</p>}
        {offer.photos?.length > 0 && (
          <div className="mt-3 flex gap-2">
            {offer.photos.map((p) => <img key={p} src={fileUrl(p)} alt="Foto do pedido" className="h-20 w-20 rounded-xl object-cover" />)}
          </div>
        )}
        <p className="mt-5 text-center text-lg text-slate-700">Tempo para aceitar</p>
        <p className="text-center font-display text-5xl font-extrabold text-orange-600 tabular-nums" data-testid="offer-countdown">{mm}:{ss}</p>
        <button disabled={busy} onClick={() => act("accept")} className="po-btn-green mt-5 w-full min-h-[72px] text-2xl" data-testid="offer-accept-button">
          <Check className="h-8 w-8" /> ACEITAR PEDIDO
        </button>
        <button disabled={busy} onClick={() => act("decline")} className="po-btn-outline mt-3 w-full" data-testid="offer-decline-button">
          <X className="h-6 w-6" /> Não posso agora
        </button>
      </div>
    </div>
  );
}

export default function OfferWatcher() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const enabled = !!user?.provider && user.provider.status === "approved";
  const { data = [] } = useQuery({
    queryKey: ["offers"],
    queryFn: () => api.get("/provider/offers").then((r) => r.data),
    enabled,
    refetchInterval: 4000,
    refetchIntervalInBackground: true,
  });
  const [dismissed, setDismissed] = useState([]);
  const offer = data.find((o) => !dismissed.includes(o.id));
  if (!enabled || !offer) return null;
  return (
    <OfferModal
      key={offer.id}
      offer={offer}
      onDone={() => { setDismissed((d) => [...d, offer.id]); qc.invalidateQueries({ queryKey: ["offers"] }); }}
    />
  );
}
